import { useEffect, useState, useRef } from 'react';
import { Play, CheckCircle2, TrendingUp, Sparkles, Coffee, CheckCircle, Clock, Trash2, MinusCircle, RotateCcw, Loader2, ChevronLeft, ChevronRight, Calendar, X, Info, ExternalLink, Bell } from 'lucide-react';
import { Habit } from '@/types';
import { useStore } from '@/store/useStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, addDays, subDays, isToday, startOfDay, parseISO } from 'date-fns';
import { nl, fr, enUS } from 'date-fns/locale';

// Parse duration string like "5 min" to minutes
function parseDuration(duration: string | null | undefined): number | null {
  if (!duration) return null;
  const match = duration.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

// Format seconds to mm:ss
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Day of week mapping
const DAY_MAP: Record<number, string> = {
  0: 'zo', 1: 'ma', 2: 'di', 3: 'wo', 4: 'do', 5: 'vr', 6: 'za',
};

// Get date string for X days ago
function getDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
}

// Parse time string "HH:MM" to minutes since midnight
function parseTime(time: string | undefined): number | null {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

// Get current time in minutes since midnight
function getCurrentMinutes(): number {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

// Check if habit is overdue (past scheduled time or no time set)
function isOverdue(habit: { targetTime?: string | null }, customTime?: string | null): boolean {
  const time = customTime ?? habit.targetTime;
  const scheduledMinutes = parseTime(time ?? undefined);

  if (scheduledMinutes === null) return true; // No time = treat as "do now"

  const currentMinutes = getCurrentMinutes();
  // Overdue if current time is past scheduled time (with 30 min grace)
  return currentMinutes >= scheduledMinutes + 30;
}

// Check if habit is coming up (within 1 hour before scheduled time)
function isComingUp(habit: { targetTime?: string | null }, customTime?: string | null): boolean {
  const time = customTime ?? habit.targetTime;
  const scheduledMinutes = parseTime(time ?? undefined);

  if (scheduledMinutes === null) return false; // No time = not "coming up"

  const currentMinutes = getCurrentMinutes();
  // Coming up if within 1 hour before to 30 min after scheduled time
  return currentMinutes >= scheduledMinutes - 60 && currentMinutes < scheduledMinutes + 30;
}

export default function CoachPage() {
  const { settings, progress, templates, toggleHabitCompletion, startHabitProgress, skipHabit } = useStore();
  const lang = settings.language;
  const locale = lang === 'nl' ? nl : lang === 'fr' ? fr : enUS;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Date navigation state - initialize from URL param if present
  const initialDate = searchParams.get('date');
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initialDate) {
      try {
        return parseISO(initialDate);
      } catch {
        return new Date();
      }
    }
    return new Date();
  });
  const currentDateStr = format(selectedDate, 'yyyy-MM-dd');

  // Info popup state
  const [infoHabit, setInfoHabit] = useState<Habit | null>(null);
  const isSelectedToday = isToday(selectedDate);

  // Timer state for busy habits
  const [habitStartTimes, setHabitStartTimes] = useState<Record<string, number>>({});
  const [elapsedTimes, setElapsedTimes] = useState<Record<string, number>>({});
  const [completedTimers, setCompletedTimers] = useState<Set<string>>(new Set());

  const selectedTemplates = templates.filter((t) => (progress.selectedTemplates || []).includes(t.id));
  const allHabits = selectedTemplates.flatMap((t) => t.habits);
  const dateCompletions = progress.completions.filter((c) => c.date === currentDateStr && c.completed);
  const completedIds = dateCompletions.map((c) => c.habitId);

  // Get day of week for selected date
  const dayCode = DAY_MAP[selectedDate.getDay()];
  const defaultDays = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

  // Filter habits: not disabled AND scheduled for this day (or one-time)
  const activeHabits = allHabits.filter((h) => {
    if (progress.customHabits?.[h.id]?.disabled) return false;

    const custom = progress.customHabits?.[h.id];
    const isOneTime = custom?.oneTime ?? h.oneTime ?? false;

    // One-time habits show on all days until completed
    if (isOneTime) return true;

    // Check if scheduled for this day
    const effectiveDays = custom?.days || h.days || defaultDays;
    return effectiveDays.includes(dayCode);
  });
  const skippedOnDate = (habitId: string) =>
    (progress.skippedHabits?.[habitId] || []).includes(currentDateStr);
  const busyOnDate = (habitId: string) =>
    (progress.busyHabits?.[habitId] || []).includes(currentDateStr);
  const pendingHabits = activeHabits.filter((h) => !completedIds.includes(h.id) && !skippedOnDate(h.id) && !busyOnDate(h.id));
  const busyHabits = activeHabits.filter((h) => busyOnDate(h.id) && !completedIds.includes(h.id));
  const doneHabits = activeHabits.filter((h) => completedIds.includes(h.id));
  const skippedHabits = activeHabits.filter((h) => skippedOnDate(h.id) && !completedIds.includes(h.id));

  // Timer helper functions
  const startTimer = (habitId: string) => {
    setHabitStartTimes((prev) => ({ ...prev, [habitId]: Date.now() }));
    setCompletedTimers((prev) => {
      const next = new Set(prev);
      next.delete(habitId);
      return next;
    });
  };

  const stopTimer = (habitId: string) => {
    setHabitStartTimes((prev) => {
      const next = { ...prev };
      delete next[habitId];
      return next;
    });
    setElapsedTimes((prev) => {
      const next = { ...prev };
      delete next[habitId];
      return next;
    });
  };

  // Play alarm sound when timer completes
  const playAlarm = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      // Second beep
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.5);
      }, 200);
    } catch (e) {
      // Audio not supported
    }
  };

  // Update elapsed times every second
  useEffect(() => {
    if (Object.keys(habitStartTimes).length === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const newElapsed: Record<string, number> = {};

      Object.entries(habitStartTimes).forEach(([habitId, startTime]) => {
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        newElapsed[habitId] = elapsedSeconds;

        // Check if timer just completed
        const habit = allHabits.find((h) => h.id === habitId);
        if (habit) {
          const customHabit = progress.customHabits?.[habitId];
          const duration = customHabit?.duration || habit.duration;
          const durationMinutes = parseDuration(duration);

          if (durationMinutes && elapsedSeconds >= durationMinutes * 60 && !completedTimers.has(habitId)) {
            playAlarm();
            setCompletedTimers((prev) => new Set([...prev, habitId]));
          }
        }
      });

      setElapsedTimes(newElapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [habitStartTimes, allHabits, progress.customHabits, completedTimers]);

  // Start/stop timers when busy habits change
  useEffect(() => {
    const busyIds = busyHabits.map((h) => h.id);

    // Start timers for newly busy habits
    busyIds.forEach((id) => {
      if (!habitStartTimes[id]) {
        startTimer(id);
      }
    });

    // Stop timers for habits that are no longer busy
    Object.keys(habitStartTimes).forEach((id) => {
      if (!busyIds.includes(id)) {
        stopTimer(id);
      }
    });
  }, [busyHabits.map((h) => h.id).join(',')]);

  // Filter habits by timing
  const overdueHabits = pendingHabits.filter((h) =>
    isOverdue(h, progress.customHabits?.[h.id]?.targetTime)
  );
  const comingUpHabits = pendingHabits.filter((h) =>
    isComingUp(h, progress.customHabits?.[h.id]?.targetTime)
  );
  const laterHabits = pendingHabits.filter((h) =>
    !isOverdue(h, progress.customHabits?.[h.id]?.targetTime) &&
    !isComingUp(h, progress.customHabits?.[h.id]?.targetTime)
  );

  // Auto-skip habits from 2+ days ago
  const twoDaysAgo = getDaysAgo(2);
  useEffect(() => {
    if (!progress.startDate) return;

    // Get all dates from start to 2 days ago
    const startDate = new Date(progress.startDate);
    const cutoffDate = new Date(twoDaysAgo);

    activeHabits.forEach((habit) => {
      // Check each day from start to 2 days ago
      const currentDate = new Date(startDate);
      while (currentDate <= cutoffDate) {
        const dateStr = currentDate.toISOString().split('T')[0];

        // Check if habit was completed or skipped on this day
        const wasCompleted = progress.completions.some(
          (c) => c.habitId === habit.id && c.date === dateStr && c.completed
        );
        const wasSkipped = (progress.skippedHabits?.[habit.id] || []).includes(dateStr);

        // Auto-skip if not completed and not already skipped
        if (!wasCompleted && !wasSkipped) {
          skipHabit(habit.id, dateStr);
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }
    });
  }, [progress.startDate, activeHabits.length]); // Only run when templates change

  const done = doneHabits.length;
  const busy = busyHabits.length;
  const skipped = skippedHabits.length;
  const total = activeHabits.length;
  const allDone = done + skipped === total && total > 0;
  const isFirstTime = progress.completions.length === 0;
  const nothingDoneToday = done === 0 && total > 0;

  // Get random tip from template
  const template = selectedTemplates[0];
  const tips = template?.tips || [];
  const randomTip = tips.length > 0 ? tips[Math.floor(Math.random() * tips.length)] : null;

  // Mark habit as done (with optional time tracking)
  const markDone = (habitId: string) => {
    // Calculate time spent in minutes if we have a start time
    const elapsedSeconds = elapsedTimes[habitId];
    const timeSpentMinutes = elapsedSeconds ? Math.ceil(elapsedSeconds / 60) : undefined;
    toggleHabitCompletion(habitId, currentDateStr, timeSpentMinutes);
    // Clear the timer state
    stopTimer(habitId);
  };

  // Date navigation helpers
  const yesterday = subDays(startOfDay(new Date()), 1);
  const canGoBack = startOfDay(selectedDate) > yesterday; // Can only go back to yesterday
  const goToPreviousDay = () => canGoBack && setSelectedDate(subDays(selectedDate, 1));
  const goToNextDay = () => setSelectedDate(addDays(selectedDate, 1));
  const goToToday = () => setSelectedDate(new Date());

  // Check if viewing a date that's too old to edit (2+ days ago)
  const daysAgo = Math.floor((startOfDay(new Date()).getTime() - startOfDay(selectedDate).getTime()) / (1000 * 60 * 60 * 24));
  const isReadOnly = daysAgo >= 2; // Day before yesterday and earlier = read-only
  const isFutureDate = startOfDay(selectedDate) > startOfDay(new Date());

  // Greeting based on state
  const getGreeting = () => {
    if (allDone) {
      return lang === 'nl'
        ? 'Geweldig werk vandaag! 🎉'
        : lang === 'fr'
        ? 'Excellent travail aujourd\'hui! 🎉'
        : 'Amazing work today! 🎉';
    }
    if (done > 0) {
      return lang === 'nl'
        ? `Goed bezig! Nog ${total - done} te gaan.`
        : lang === 'fr'
        ? `Bien joué! Encore ${total - done} à faire.`
        : `Good progress! ${total - done} more to go.`;
    }
    if (total > 0) {
      return lang === 'nl'
        ? 'Klaar om te beginnen?'
        : lang === 'fr'
        ? 'Prêt à commencer?'
        : 'Ready to start?';
    }
    return lang === 'nl'
      ? 'Welkom! Laten we beginnen.'
      : lang === 'fr'
      ? 'Bienvenue! Commençons.'
      : 'Welcome! Let\'s get started.';
  };

  return (
    <div className="min-h-screen pb-24 px-4 py-6">
      {/* Date Navigation */}
      <div className="max-w-md mx-auto mb-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm p-3 flex items-center justify-between">
          <button
            onClick={goToPreviousDay}
            disabled={!canGoBack}
            className={`p-2 rounded-lg transition-colors ${
              canGoBack
                ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                : 'text-gray-300 cursor-not-allowed'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex-1 text-center">
            <div className="font-medium text-gray-800 capitalize">
              {isSelectedToday
                ? (lang === 'nl' ? 'Vandaag' : lang === 'fr' ? 'Aujourd\'hui' : 'Today')
                : format(selectedDate, 'EEEE d MMMM', { locale })}
            </div>
            {!isSelectedToday && (
              <button
                onClick={goToToday}
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                {lang === 'nl' ? 'Terug naar vandaag' : lang === 'fr' ? 'Retour à aujourd\'hui' : 'Back to today'}
              </button>
            )}
          </div>

          <button
            onClick={goToNextDay}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <button
            onClick={() => navigate(`/tracker?date=${currentDateStr}`)}
            className="ml-2 p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title={lang === 'nl' ? 'Kalender' : lang === 'fr' ? 'Calendrier' : 'Calendar'}
          >
            <Calendar className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="text-center mb-6">
        <span className="text-5xl mb-4 block">🤖</span>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          {lang === 'nl' ? 'Je Coach' : lang === 'fr' ? 'Ton Coach' : 'Your Coach'}
        </h1>
        <p className="text-gray-600">{getGreeting()}</p>
      </div>

      {/* Read-only notice for old dates */}
      {isReadOnly && (
        <div className="max-w-md mx-auto mb-4">
          <div className="bg-slate-100 text-slate-600 text-sm text-center py-2 px-4 rounded-xl">
            {lang === 'nl' ? 'Alleen bekijken - te oud om te bewerken' : lang === 'fr' ? 'Lecture seule - trop ancien pour modifier' : 'View only - too old to edit'}
          </div>
        </div>
      )}

      {/* Progress bar */}
      {total > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>{done}/{total} {lang === 'nl' ? 'gedaan' : lang === 'fr' ? 'fait' : 'done'}</span>
            {progress.streak > 0 && <span>{progress.streak} 🔥</span>}
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-500"
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* First-time onboarding */}
      {(isFirstTime || nothingDoneToday) && total > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <div className="bg-gradient-to-br from-primary-50 to-cyan-50 rounded-2xl p-5">
            {isFirstTime ? (
              <>
                <h2 className="font-semibold text-gray-800 mb-2">
                  {lang === 'nl' ? '👋 Welkom bij EasyHabits!' : lang === 'fr' ? '👋 Bienvenue sur EasyHabits!' : '👋 Welcome to EasyHabits!'}
                </h2>
                <p className="text-sm text-gray-600 mb-3">
                  {lang === 'nl'
                    ? 'Bouw betere gewoontes op, één dag tegelijk. Geen stress, geen account nodig.'
                    : lang === 'fr'
                    ? 'Développez de meilleures habitudes, un jour à la fois. Pas de stress, pas de compte requis.'
                    : 'Build better habits, one day at a time. No stress, no account needed.'}
                </p>
                <div className="text-sm text-gray-600 space-y-2">
                  <p>
                    <span className="font-medium">1.</span>{' '}
                    {lang === 'nl'
                      ? 'Tik op een gewoonte om te starten'
                      : lang === 'fr'
                      ? 'Appuyez sur une habitude pour commencer'
                      : 'Tap a habit to start'}
                  </p>
                  <p>
                    <span className="font-medium">2.</span>{' '}
                    {lang === 'nl'
                      ? 'Markeer als "Gedaan" wanneer je klaar bent'
                      : lang === 'fr'
                      ? 'Marquez comme "Fait" quand vous avez terminé'
                      : 'Mark as "Done" when finished'}
                  </p>
                  <p>
                    <span className="font-medium">3.</span>{' '}
                    {lang === 'nl'
                      ? 'Bouw je streak op door elke dag terug te komen!'
                      : lang === 'fr'
                      ? 'Construisez votre streak en revenant chaque jour!'
                      : 'Build your streak by coming back daily!'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-700">
                  {lang === 'nl'
                    ? '☀️ Goedemorgen! Klaar voor een nieuwe dag?'
                    : lang === 'fr'
                    ? '☀️ Bonjour! Prêt pour une nouvelle journée?'
                    : '☀️ Good morning! Ready for a new day?'}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* All done celebration */}
      {allDone && (
        <div className="max-w-md mx-auto mb-6">
          <div className="bg-gradient-to-br from-primary-50 to-emerald-50 rounded-2xl p-6 text-center">
            <Coffee className="w-12 h-12 text-primary-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-gray-800 mb-2">
              {lang === 'nl'
                ? 'Je hebt alle gewoontes voltooid!'
                : lang === 'fr'
                ? 'Tu as complété toutes tes habitudes!'
                : 'You\'ve completed all your habits!'}
            </p>
            <p className="text-gray-600 mb-4">
              {lang === 'nl'
                ? 'Geniet van je welverdiende rust. Tot morgen! 💪'
                : lang === 'fr'
                ? 'Profite de ton repos bien mérité. À demain! 💪'
                : 'Enjoy your well-deserved rest. See you tomorrow! 💪'}
            </p>
            <button
              onClick={() => navigate('/stats')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors"
            >
              <TrendingUp className="w-4 h-4" />
              {lang === 'nl' ? 'Bekijk statistieken' : lang === 'fr' ? 'Voir statistiques' : 'View stats'}
            </button>
          </div>
        </div>
      )}

      {/* Busy / In progress habits */}
      {busyHabits.length > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 px-1">
            {lang === 'nl' ? 'Bezig' : lang === 'fr' ? 'En cours' : 'In progress'}
          </h2>
          <div className="space-y-3">
            {busyHabits.map((habit) => {
              const habitName = habit.name[lang] || habit.name.en;
              const customHabit = progress.customHabits?.[habit.id];
              const duration = customHabit?.duration || habit.duration;
              const durationMinutes = parseDuration(duration);
              const elapsedSeconds = elapsedTimes[habit.id] || 0;
              const elapsedMinutes = Math.floor(elapsedSeconds / 60);
              const isTimerComplete = durationMinutes ? elapsedSeconds >= durationMinutes * 60 : false;

              return (
                <div
                  key={habit.id}
                  className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden ring-2 ring-offset-2 ${
                    isTimerComplete ? 'ring-emerald-400' : 'ring-amber-400'
                  }`}
                >
                  <div className="px-4 py-3 flex items-center gap-3">
                    {isTimerComplete ? (
                      <Bell className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-lg">{habit.icon}</span>
                        <h3 className="font-semibold text-gray-800">{habitName}</h3>
                        <button
                          onClick={() => setInfoHabit(habit)}
                          className="p-0.5 rounded text-gray-400 hover:text-primary-500 transition-colors"
                          title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </div>
                      {/* Timer display */}
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-sm font-mono ${isTimerComplete ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {formatTime(elapsedSeconds)}
                        </span>
                        {durationMinutes && (
                          <>
                            <span className="text-gray-400">/</span>
                            <span className="text-sm text-gray-500">{durationMinutes} min</span>
                          </>
                        )}
                        {isTimerComplete && (
                          <span className="text-xs text-emerald-600 font-medium">
                            ✓ {lang === 'nl' ? 'Klaar!' : lang === 'fr' ? 'Terminé!' : 'Done!'}
                          </span>
                        )}
                      </div>
                      {/* Progress bar if duration is set */}
                      {durationMinutes && (
                        <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${isTimerComplete ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min((elapsedSeconds / (durationMinutes * 60)) * 100, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                    {/* Cancel and Done buttons */}
                    <button
                      onClick={() => startHabitProgress(habit.id, currentDateStr)}
                      className="p-2 rounded-xl bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"
                      title={lang === 'nl' ? 'Annuleren' : lang === 'fr' ? 'Annuler' : 'Cancel'}
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => markDone(habit.id)}
                      className="p-2.5 rounded-xl bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                      title={lang === 'nl' ? 'Gedaan' : lang === 'fr' ? 'Terminé' : 'Done'}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Overdue / Do now habits */}
      {overdueHabits.length > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 px-1">
            {lang === 'nl' ? 'Nu te doen' : lang === 'fr' ? 'À faire maintenant' : 'Do now'}
          </h2>
          <div className="space-y-3">
            {overdueHabits.map((habit, index) => {
              const habitName = habit.name[lang] || habit.name.en;
              const customHabit = progress.customHabits?.[habit.id];
              const time = customHabit?.targetTime ?? habit.targetTime;
              const duration = customHabit?.duration || habit.duration;
              const isFirst = index === 0 && comingUpHabits.length === 0 && busyHabits.length === 0;

              return (
                <div
                  key={habit.id}
                  className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden ${
                    isFirst ? 'ring-2 ring-primary-500 ring-offset-2' : ''
                  }`}
                >
                  {/* Time badge at top (if overdue) */}
                  {time && (
                    <div className="px-4 pt-3 pb-1">
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600">
                        <Clock className="w-4 h-4" />
                        {time}
                        {duration && <span className="text-gray-400 font-normal">· {duration}</span>}
                      </span>
                    </div>
                  )}
                  {/* Habit name and action buttons */}
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-lg">{habit.icon}</span>
                        <h3 className="font-semibold text-gray-800">{habitName}</h3>
                        <button
                          onClick={() => setInfoHabit(habit)}
                          className="p-0.5 rounded text-gray-400 hover:text-primary-500 transition-colors"
                          title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </div>
                      {!time && duration && (
                        <span className="text-xs text-gray-500">{duration}</span>
                      )}
                    </div>
                    {/* Icon buttons - only show if not read-only */}
                    {!isReadOnly && (
                      <>
                        <button
                          onClick={() => skipHabit(habit.id, currentDateStr)}
                          className="p-2 rounded-xl bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-500 transition-colors"
                          title={lang === 'nl' ? 'Overslaan' : lang === 'fr' ? 'Passer' : 'Skip'}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => startHabitProgress(habit.id, currentDateStr)}
                          className={`p-2.5 rounded-xl transition-colors ${
                            isFirst
                              ? 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                          title={lang === 'nl' ? 'Start' : lang === 'fr' ? 'Commencer' : 'Start'}
                        >
                          <Play className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => markDone(habit.id)}
                          className="p-2.5 rounded-xl bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                          title={lang === 'nl' ? 'Gedaan' : lang === 'fr' ? 'Terminé' : 'Done'}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Coming up habits */}
      {comingUpHabits.length > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 px-1">
            {lang === 'nl' ? 'Binnenkort' : lang === 'fr' ? 'Bientôt' : 'Coming up'}
          </h2>
          <div className="space-y-3">
            {comingUpHabits.map((habit, index) => {
              const habitName = habit.name[lang] || habit.name.en;
              const customHabit = progress.customHabits?.[habit.id];
              const time = customHabit?.targetTime ?? habit.targetTime;
              const duration = customHabit?.duration || habit.duration;
              const isFirst = index === 0 && overdueHabits.length === 0 && busyHabits.length === 0;

              return (
                <div
                  key={habit.id}
                  className={`bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden ${
                    isFirst ? 'ring-2 ring-primary-500 ring-offset-2' : ''
                  }`}
                >
                  {/* Time badge at top */}
                  <div className="px-4 pt-3 pb-1">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
                      <Clock className="w-4 h-4" />
                      {time}
                      {duration && <span className="text-gray-400 font-normal">· {duration}</span>}
                    </span>
                  </div>
                  {/* Habit name and action buttons */}
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-lg">{habit.icon}</span>
                        <h3 className="font-semibold text-gray-800">{habitName}</h3>
                        <button
                          onClick={() => setInfoHabit(habit)}
                          className="p-0.5 rounded text-gray-400 hover:text-primary-500 transition-colors"
                          title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {/* Icon buttons */}
                    <button
                      onClick={() => startHabitProgress(habit.id, currentDateStr)}
                      className={`p-2.5 rounded-xl transition-colors ${
                        isFirst
                          ? 'bg-primary-100 text-primary-600 hover:bg-primary-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      title={lang === 'nl' ? 'Start' : lang === 'fr' ? 'Commencer' : 'Start'}
                    >
                      <Play className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => markDone(habit.id)}
                      className="p-2.5 rounded-xl bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                      title={lang === 'nl' ? 'Gedaan' : lang === 'fr' ? 'Terminé' : 'Done'}
                    >
                      <CheckCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Later habits (more than 1 hour away) */}
      {laterHabits.length > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 px-1">
            {lang === 'nl' ? 'Later' : lang === 'fr' ? 'Plus tard' : 'Later'}
          </h2>
          <div className="space-y-3">
            {laterHabits.map((habit) => {
              const habitName = habit.name[lang] || habit.name.en;
              const customHabit = progress.customHabits?.[habit.id];
              const time = customHabit?.targetTime ?? habit.targetTime;
              const duration = customHabit?.duration || habit.duration;

              return (
                <div
                  key={habit.id}
                  className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden"
                >
                  {/* Time badge at top */}
                  {time && (
                    <div className="px-4 pt-3 pb-1">
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500">
                        <Clock className="w-4 h-4" />
                        {time}
                        {duration && <span className="text-gray-400 font-normal">· {duration}</span>}
                      </span>
                    </div>
                  )}
                  {/* Habit name and action buttons */}
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-lg">{habit.icon}</span>
                        <h3 className="font-semibold text-gray-800">{habitName}</h3>
                        <button
                          onClick={() => setInfoHabit(habit)}
                          className="p-0.5 rounded text-gray-400 hover:text-primary-500 transition-colors"
                          title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                        >
                          <Info className="w-4 h-4" />
                        </button>
                      </div>
                      {!time && duration && (
                        <span className="text-xs text-gray-500">{duration}</span>
                      )}
                    </div>
                    {/* Icon buttons */}
                    {!isReadOnly && (
                      <>
                        <button
                          onClick={() => skipHabit(habit.id, currentDateStr)}
                          className="p-2 rounded-xl bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-500 transition-colors"
                          title={lang === 'nl' ? 'Overslaan' : lang === 'fr' ? 'Passer' : 'Skip'}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => startHabitProgress(habit.id, currentDateStr)}
                          className="p-2.5 rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                          title={lang === 'nl' ? 'Start' : lang === 'fr' ? 'Commencer' : 'Start'}
                        >
                          <Play className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => markDone(habit.id)}
                          className="p-2.5 rounded-xl bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors"
                          title={lang === 'nl' ? 'Gedaan' : lang === 'fr' ? 'Terminé' : 'Done'}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Completed habits */}
      {doneHabits.length > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 px-1">
            {lang === 'nl' ? 'Voltooid' : lang === 'fr' ? 'Terminé' : 'Completed'}
          </h2>
          <div className="space-y-2">
            {doneHabits.map((habit) => {
              const habitName = habit.name[lang] || habit.name.en;

              return (
                <div
                  key={habit.id}
                  className="w-full bg-white/60 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary-500" />
                  <span className="text-lg">{habit.icon}</span>
                  <div className="flex-1 flex items-center gap-1.5">
                    <span className="text-gray-600 line-through">{habitName}</span>
                    <button
                      onClick={() => setInfoHabit(habit)}
                      className="p-0.5 rounded text-gray-400 hover:text-primary-500 transition-colors"
                      title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>
                  {!isReadOnly && (
                    <button
                      onClick={() => markDone(habit.id)}
                      className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                      title={lang === 'nl' ? 'Ongedaan maken' : lang === 'fr' ? 'Annuler' : 'Undo'}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Skipped habits */}
      {skippedHabits.length > 0 && (
        <div className="max-w-md mx-auto mb-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 px-1">
            {lang === 'nl' ? 'Overgeslagen' : lang === 'fr' ? 'Passé' : 'Skipped'}
          </h2>
          <div className="space-y-2">
            {skippedHabits.map((habit) => {
              const habitName = habit.name[lang] || habit.name.en;

              return (
                <div
                  key={habit.id}
                  className="w-full bg-white/40 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3"
                >
                  <MinusCircle className="w-5 h-5 text-gray-400" />
                  <span className="text-lg opacity-50">{habit.icon}</span>
                  <div className="flex-1 flex items-center gap-1.5">
                    <span className="text-gray-400">{habitName}</span>
                    <button
                      onClick={() => setInfoHabit(habit)}
                      className="p-0.5 rounded text-gray-300 hover:text-primary-500 transition-colors"
                      title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>
                  {!isReadOnly && (
                    <button
                      onClick={() => skipHabit(habit.id, currentDateStr)}
                      className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                      title={lang === 'nl' ? 'Ongedaan maken' : lang === 'fr' ? 'Annuler' : 'Undo'}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No habits set up */}
      {total === 0 && (
        <div className="max-w-md mx-auto">
          <button
            onClick={() => navigate('/onboarding')}
            className="w-full p-4 rounded-2xl bg-primary-500 text-white shadow-lg hover:bg-primary-600 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-white/20">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-semibold">
                  {lang === 'nl' ? 'Kies een template' : lang === 'fr' ? 'Choisir un modèle' : 'Choose a template'}
                </h3>
                <p className="text-sm text-white/80">
                  {lang === 'nl'
                    ? 'Start met een van onze habit templates'
                    : lang === 'fr'
                    ? 'Commencez avec un de nos modèles'
                    : 'Start with one of our habit templates'}
                </p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Tip of the day */}
      {randomTip && !allDone && pendingHabits.length > 0 && (
        <div className="mt-6 max-w-md mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 mb-1">
                  {lang === 'nl' ? 'Tip' : lang === 'fr' ? 'Conseil' : 'Tip'}
                </p>
                <p className="text-sm text-amber-700">
                  {randomTip[lang] || randomTip.en}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Easy Cyber Protection link */}
      <div className="mt-6 max-w-md mx-auto">
        <a
          href="https://easycyberprotection.com"
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-primary-50 rounded-xl p-4 border border-primary-100 hover:bg-primary-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔒</span>
            <div className="flex-1">
              <p className="font-medium text-primary-800">
                {lang === 'nl' ? 'Maak van cyberveiligheid een gewoonte' : lang === 'fr' ? 'Faites de la cybersécurité une habitude' : 'Make cybersecurity a habit'}
              </p>
              <p className="text-sm text-primary-600 flex items-center gap-1">
                {lang === 'nl' ? 'Ontdek Easy Cyber Protection' : lang === 'fr' ? 'Découvrez Easy Cyber Protection' : 'Discover Easy Cyber Protection'}
                <ExternalLink className="w-3 h-3" />
              </p>
            </div>
          </div>
        </a>
      </div>

      {/* Habit Info Popup */}
      {infoHabit && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-4"
          onClick={() => setInfoHabit(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{infoHabit.icon}</span>
                <h3 className="font-semibold text-gray-800">
                  {infoHabit.name[lang] || infoHabit.name.en}
                </h3>
              </div>
              <button
                onClick={() => setInfoHabit(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Description */}
              {infoHabit.description && (infoHabit.description[lang] || infoHabit.description.en) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">
                    {lang === 'nl' ? 'Beschrijving' : lang === 'fr' ? 'Description' : 'Description'}
                  </h4>
                  <p className="text-gray-700">
                    {infoHabit.description[lang] || infoHabit.description.en}
                  </p>
                </div>
              )}

              {/* Schedule info */}
              {(infoHabit.targetTime || infoHabit.duration) && (
                <div className="flex flex-wrap gap-3">
                  {infoHabit.targetTime && (
                    <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">{infoHabit.targetTime}</span>
                    </div>
                  )}
                  {infoHabit.duration && (
                    <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                      <span className="text-sm text-gray-700">{infoHabit.duration}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
